import { Slider } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { VideoJsPlayer } from 'video.js';
import { VideoActions } from '../../../hooks/useVideoJs';

import { colors as ThemeColors } from '../../const/Styles';
import PlayController from './PlayControler';
import PlaybackPositionSaveButton from '../PlaybackPositionSaveButton';

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: fit-content;
    margin: 0 auto;
    align-items: center;
`;

const PlayControlContainer = styled(ContentContainer)`
    width: 100%;
    gap: 4px;
`;

const ToolContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
`;

const StyledSeekbar = styled(Slider)`
    color: ${ThemeColors.footerIconSelected};
    padding: '8px 0';

    .MuiSlider-thumb {
        width: 24px;
        height: 24px;
        margin-top: -10px;
    }
`;

type Props = {
    actions: VideoActions;
    player?: VideoJsPlayer;
    isBackgroundDark?: boolean;
    onClick: () => void;
};

const SeekbarPlayer: React.FC<Props> = ({
    player,
    actions,
    isBackgroundDark,
    onClick,
}) => {
    const [playing, setPlaying] = useState<boolean>(false);
    useEffect(() => {
        const endedPlayer = () => {
            setPlaying(false);
        };

        if (player) {
            player.on('ended', endedPlayer);
        }

        return () => {
            if (player) {
                player.off('ended', endedPlayer);
            }
        };
    }, [player]);
    const startVideo = useCallback(() => {
        if (player?.paused()) {
            player?.play();
            setPlaying(true);
        } else {
            player?.pause();
            setPlaying(false);
        }
    }, [player]);

    const [currentSeekbarRatio, setCurrentSeekbarRatio] = useState(0);

    const intervalId = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
        }

        intervalId.current = setInterval(() => {
            if (!player) {
                return;
            }
            if (isNaN(player.currentTime()) || isNaN(player.duration())) {
                return;
            }
            setCurrentSeekbarRatio(
                Math.round((player.currentTime() / player.duration()) * 100),
            );
        }, 10);
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
    }, [player, setCurrentSeekbarRatio]);

    const onSeekbarChange = useCallback(
        (event, seekbarValue) => {
            actions.skipByRatio(seekbarValue);
        },
        [actions],
    );
    return (
        <PlayControlContainer>
            <ToolContainer>
                <StyledSeekbar
                    min={0}
                    max={100}
                    value={currentSeekbarRatio}
                    onChange={onSeekbarChange}
                />
                <PlaybackPositionSaveButton onClick={onClick} />
            </ToolContainer>
            <PlayController
                isBackgroundDark={isBackgroundDark}
                actions={actions}
                onStart={startVideo}
                isPlaying={playing}
                useFullScreen={true}
                seekbarAction={{
                    duration: player?.duration(),
                    onChangeSlider: undefined,
                }}
            />
        </PlayControlContainer>
    );
};

export default SeekbarPlayer;
